import { make } from 'vuex-pathify'
import { api, print } from '@/api'
import dottie from 'dottie'
import questionsJSON from '@/data/questions.json'

import sites from '@/data/sites'

/**
 * semantics try align with DDD
 * @see https://www.thenativeweb.io/blog/2017-10-25-09-46-ddd-and-co-part-1-whats-wrong-with-crud/
 * */

const intersection = (array1, array2, key) => {
  const property = key || 'key'
  return array1.map((a1) => a1[property]).filter((f) => array2.includes(f))
}

const hasProperty = (object, key) => {
  return Object.hasOwnProperty.call(object, key)
}

/**
 * Retrieve "Foerderthemen" related to Gebaeude; otherwise return empty array.
 * @returns array - gebaeudefoerderthemen
 */
const foerderthemenGebaeudeRelated = () => {
  return (
    (
      (
        (questionsJSON.find((q) => q.key === 'foerderthemen') || {})
          .optionGroups || []
      ).find((g) => g.key === 'FWW_MA_OP_01') || {}
    ).options || []
  )
}

// like: initial state
const state = {
  selection: {
    site: 'default'
  },
  programs: [],
  program: {},
  show: false,
  pdf: {
    blob: null,
    joinedIds: '',
    downloading: false
  }
}

// like: aggregated state
const getters = {
  filtered(state) {
    // filter region / bundeslaender / kirchenkreise
    let filtered = state.programs.filter((p) => {
      if (p.region.bundesweit.value === 'FWW_BUNDESWEIT_TRUE') return true
      // only filter, if program has kirchenkreise selected #12750
      if (
        dottie.get(state.selection, 'kirchenkreis') &&
        p.region.kirchenkreise.length
      ) {
        return intersection(
          p.region.kirchenkreise,
          state.selection.kirchenkreis
        ).length
      }
      if (dottie.get(state.selection, 'bundeslaender')) {
        return intersection(
          p.region.bundeslaender,
          state.selection.bundeslaender
        ).length
      }
    })

    // filter for kirchenkreis/bundeslaender combo if site has wizard step 2
    // #12750
    const currentSite = sites.find((s) => s.name === state.selection.site)
    if (
      currentSite &&
      currentSite.steps.includes(2) &&
      dottie.get(state.selection, 'bundeslaender')
    ) {
      filtered = filtered.filter((p) => {
        if (p.region.bundesweit.value === 'FWW_BUNDESWEIT_TRUE') return true
        return intersection(
          p.region.bundeslaender,
          state.selection.bundeslaender
        ).length
      })
    }

    // filter foerdergeber
    if (dottie.get(state.selection, 'foerdergeber')) {
      filtered = filtered.filter((p) => {
        if (state.selection.foerdergeber.includes('ALL')) return true
        return state.selection.foerdergeber.includes(p.foerdergeber.ebene_key)
      })
    }

    if (dottie.get(state.selection, 'themenfeld')) {
      filtered = filtered.filter((p) => {
        return intersection(
          p.themenfeld,
          state.selection.themenfeld,
          'rubrik_key'
        ).length
      })
    }

    let withGebaeudeThemen = []
    // handle gebaeudearten
    if (dottie.get(state.selection, 'gebaeudearten')) {
      const withGebaeudearten = filtered
        .filter((p) => {
          return p.themenfeld.find((t) => t.rubrik_key === 'FWW_TF_01')
        })
        .filter((p) => {
          return intersection(p.gebaeudearten, state.selection.gebaeudearten)
            .length
        })

      // filter foerderthemen for gebaeudearten related programs
      if (dottie.get(state.selection, 'foerderthemen')) {
        withGebaeudeThemen = withGebaeudearten.filter((p) => {
          return intersection(
            p.foerderthemen,
            state.selection.foerderthemen,
            'id'
          ).length
        })
      }
    }

    // handle other programmes
    if (dottie.get(state.selection, 'foerderthemen')) {
      // use only Foerderthemen which are unrelated to Gebaeude
      const gebaeudeFoerderthemen = foerderthemenGebaeudeRelated()
      const filteredFoerderthemen = state.selection.foerderthemen.filter(
        (t) => !gebaeudeFoerderthemen.find((g) => g.key === t)
      )

      filtered = filtered
        // filter out already handled programs
        .filter((p) => !withGebaeudeThemen.includes(p))
        // exclude programmes soley for Gebäudearten
        .filter((p) => p.themenfeld.some((t) => t.rubrik_key !== 'FWW_TF_01'))
        .filter(
          (p) =>
            intersection(p.foerderthemen, filteredFoerderthemen, 'id').length
        )
    }

    return [...filtered, ...withGebaeudeThemen].sort((a, b) => {
      if (a.title > b.title) return 1
      if (a.title < b.title) return -1
      return 0
    })
  },
  filteredIds(state, getters) {
    return getters.filtered.map((p) => p.id)
  },
  complete(state) {
    return (
      Object.entries(state.selection).filter(([k, v], i) => v.length).length ===
      Object.keys(state.selection).length
    )
  },
  ...make.getters(state)
}

// like: fires events
const mutations = {
  listed(state, { programs }) {
    state.programs = programs
  },
  listingFailed(state) {
    state.programs = []
  },
  loaded(state, { program }) {
    state.program = program
  },
  loadedFailed(state) {
    state.program = {}
  },
  removedSelection(state, { key, option }) {
    const index = state.selection[key].findIndex((o) => o === option.key)
    if (index !== -1) {
      return state.selection[key].splice(index, 1)
    }
  },
  removedConditionalFields(state, { key, values }) {
    state.selection[key] = state.selection[key].filter(
      (s) => !values.includes(s)
    )
  },
  addedSelection(state, { key, option, multi }) {
    if (!hasProperty(state.selection, key)) state.selection[key] = []
    if (!multi && state.selection[key].length > 0) {
      state.selection[key] = []
    }
    state.selection[key].push(option.key)
  },
  pdfBlobCreated(state, blob) {
    state.pdf.blob = blob
  },
  pdfIdsJoined(state, joinedIds) {
    state.pdf.joinedIds = joinedIds
  },
  pdfDownloading(state, downloading) {
    state.pdf.downloading = downloading
  },
  ...make.mutations(state)
}

// like: commands
const actions = {
  async list({ commit }, site) {
    try {
      const response = await api.get(`/foerderprogramme?mandant=${site}`)
      const programs = dottie.get(response, 'data', [])
      commit('listed', { programs })
    } catch (e) {
      commit('listingFailed')
    }
  },
  async load({ commit }, url) {
    try {
      console.log('try loading')
      const response = await api.get(`/foerderprogramme/${url}`)
      const program = dottie.get(response, 'data.data', {})
      commit('loaded', { program })
    } catch (e) {
      commit('loadedFailed')
    }
  },
  toggleSelect({ commit, state }, { option, key, multi }) {
    if (hasProperty(state.selection, key)) {
      if (state.selection[key].includes(option.key)) {
        if (!multi) return
        return commit('removedSelection', { key, option })
      }
    }
    commit('addedSelection', { key, option, multi })
  },
  removeConditionalFields({ commit }, { key, values }) {
    commit('removedConditionalFields', { key, values })
  },
  async fetchPDFSearchResult({ commit, state, getters }) {
    const ids = getters.filteredIds

    if (ids.length && state.pdf.joinedIds === ids.join()) return

    commit('pdfDownloading', true)

    try {
      const result = await print.post(
        '',
        {
          ids: ids,
          appHost: location.hostname,
          site: state.selection.site
        },
        { responseType: 'blob' }
      )
      if (result.status === 200) {
        commit('pdfIdsJoined', ids.join())
        commit('pdfBlobCreated', result.data)
        commit('pdfDownloading', false)
      }
    } catch (error) {
      console.log('fetchPDFSearchResult: error requsting data', error)
      commit('pdfDownloading', false)
    }
  }
}

// like: domain
export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
